import type { IncidentReportContext } from '@incidents/entities/incident';
import type {
  IncidentAttachment,
  IncidentCategory,
  IncidentSeverity,
  ItineraryReferenceMatch,
} from '@travel-planner/shared/schemas';
import type { ChangeEvent } from 'react';
import type { SelectOption } from '@/shared/ui';
import { useAuthData } from '@auth/features/auth';
import {
  buildReference,
  detectPageContext,
  detectPageItineraryIds,
  incidentCategoryValues,
  incidentSeverityValues,
  isAudioName,
  isDocumentName,
  isVideoName,
  useIncidentDrawerStore,
} from '@incidents/entities/incident';
import {
  AudioLines,
  Compass,
  FileText,
  MapPin,
  Paperclip,
  Video,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  Select,
  ServerSearchSelect,
  Text,
  Textarea,
  toastStore,
} from '@/shared/ui';
import { useUploadThing } from '@/shared/uploadthing';
import { validateAttachmentFile } from '../lib/validate-attachment';
import { useCreateIncident } from '../model/use-create-incident';
import {
  itineraryOptionLabel,
  makeSeededItinerarySearch,
  useItineraryLabel,
} from '../model/use-itinerary-search';
import css from './report-incident-modal.module.scss';

const humanize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');

const CATEGORY_OPTIONS: SelectOption[] = incidentCategoryValues.map((v) => ({
  value: v,
  label: humanize(v),
}));
const SEVERITY_OPTIONS: SelectOption[] = incidentSeverityValues.map((v) => ({
  value: v,
  label: humanize(v),
}));

// Attachments are hidden from the Report modal for now (XT-143 cleanup) until
// the final attachment experience is ready. The upload wiring, DB fields, and
// backend support are all intact — flip to `true` to restore the section.
// Typed const so the upload logic below stays compiled + linted.
const SHOW_ATTACHMENTS_UI: boolean = false;

const Required = () => (
  <Text aria-hidden as="span" className={css.req}>
    *
  </Text>
);

const shortId = (id?: string): string => (id ? `${id.slice(0, 8)}…` : '');

// Report modal — mounted once (inside the drawer). Incidents are GLOBAL: the
// modal detects the current entity/page (product / itinerary / operation /
// any page) and links the incident to it. An itinerary is only required when
// there is no other entity AND the reporter chooses to link one. Attachments
// (screenshot / screen recording) are uploaded manually — never auto-captured.
export const ReportIncidentModal = () => {
  const reportOpen = useIncidentDrawerStore((s) => s.reportOpen);
  const scope = useIncidentDrawerStore((s) => s.scope);
  const closeReport = useIncidentDrawerStore((s) => s.closeReport);
  const createIncident = useCreateIncident();
  const { data: authData } = useAuthData();
  // File storage (UploadThing) may be unconfigured on a deployment — hide the
  // attachment affordance rather than let the user hit a 503.
  const uploadsEnabled = authData?.uploadsEnabled ?? false;

  const [context, setContext] = useState<IncidentReportContext>({});
  const [pickedItineraryId, setPickedItineraryId] = useState<string | null>(
    null
  );
  // Itineraries detected on the page behind the modal — pre-seed the picker.
  const [detectedItineraryIds, setDetectedItineraryIds] = useState<string[]>(
    []
  );
  const pickedItineraryLabel = useItineraryLabel(pickedItineraryId);
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');
  const [whatHappened, setWhatHappened] = useState('');
  const [country, setCountry] = useState('');
  const [attachments, setAttachments] = useState<IncidentAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { startUpload, isUploading } = useUploadThing('fileUploader', {
    onClientUploadComplete: (files) => {
      if (!files) return;
      setAttachments((prev) => [
        ...prev,
        ...files.map((file) => ({
          key: file.key,
          url: file.ufsUrl,
          name: file.name,
        })),
      ]);
    },
    onUploadError: (error) => {
      const notConfigured = /not configured|503|unavailable/i.test(
        error.message
      );
      toastStore.getState().show({
        message: notConfigured
          ? 'Attachments are unavailable — file storage is not configured.'
          : `Upload failed: ${error.message}`,
      });
    },
  });

  // Reset + re-capture context each time the modal opens.
  useEffect(() => {
    if (reportOpen) {
      setContext(
        scope.context ?? detectPageContext({ accountId: authData?.user.id })
      );
      setPickedItineraryId(null);
      setDetectedItineraryIds(detectPageItineraryIds());
      setCategory('');
      setSeverity('');
      setWhatHappened('');
      setAttachments([]);
    }
  }, [reportOpen, scope, authData?.user.id]);

  if (!reportOpen) return null;

  // A locked itinerary comes from an itinerary-page trigger OR a detected
  // itinerary URL. Product/operation/preview pages are NOT itineraries.
  const lockedItineraryId =
    scope.itineraryId ??
    (context.entityType === 'itinerary'
      ? (context.itineraryId ?? context.entityId)
      : undefined);
  const detectedKind = scope.itineraryId
    ? 'itinerary'
    : (context.entityType ?? 'other');
  // Only offer the optional itinerary picker when nothing else is detected.
  const showItineraryPicker = !lockedItineraryId && detectedKind === 'other';

  const finalItineraryId = lockedItineraryId ?? pickedItineraryId ?? undefined;

  const reference = buildReference({
    entityType: finalItineraryId ? 'itinerary' : context.entityType,
    entityId: context.entityId,
    itineraryId: finalItineraryId,
    itineraryRef: scope.itineraryRef,
    applicationUrl: context.href,
    pageContext: { module: context.module },
  });

  const canSubmit = Boolean(category && severity && whatHappened.trim());

  const onPickFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    const valid: File[] = [];
    for (const file of files) {
      const error = validateAttachmentFile(file);
      if (error) {
        toastStore.getState().show({ message: error });
        continue;
      }
      valid.push(file);
    }
    if (valid.length) startUpload(valid);
  };

  const removeAttachment = (key: string) =>
    setAttachments((prev) => prev.filter((a) => a.key !== key));

  const handleSubmit = () => {
    if (!canSubmit) return;
    createIncident.mutate(
      {
        itineraryId: finalItineraryId,
        entityType: finalItineraryId
          ? 'itinerary'
          : (context.entityType ?? 'other'),
        entityId: finalItineraryId ?? context.entityId ?? null,
        category: category as IncidentCategory,
        severity: severity as IncidentSeverity,
        whatHappened: whatHappened.trim(),
        country: country.trim() || undefined,
        applicationUrl:
          context.href ??
          (typeof window === 'undefined' ? null : window.location.href),
        pageContext: context,
        attachments,
      },
      {
        onSuccess: () => {
          toastStore.getState().show({ message: 'Incident reported' });
          closeReport();
        },
        onError: () =>
          toastStore
            .getState()
            .show({ message: 'Could not report the incident' }),
      }
    );
  };

  return (
    <Modal onClose={closeReport} open={reportOpen}>
      <ModalHeader onClose={closeReport} title="Report an incident" />
      <div className={css.form}>
        <Text as="p" className={css.subtitle} size="sm" variant="muted">
          Report a travel or operational incident — we'll link it to the
          itinerary and page you were on.
        </Text>

        {/* Origin — auto-detected page + entity the report came from. */}
        <div className={css.origin}>
          <Compass className={css.originIcon} />
          <div className={css.originBody}>
            <Text as="span" size="xs" variant="muted">
              Reported from
            </Text>
            <div className={css.originValue}>
              <Badge variant="booked">{context.module ?? 'Xthory'}</Badge>
              <Text
                as="span"
                className={css.originRef}
                size="xs"
                variant="muted"
              >
                {reference.label}
                {reference.kind === 'product' && context.entityId
                  ? ` · ${shortId(context.entityId)}`
                  : ''}
                {context.tab ? ` · ${context.tab}` : ''}
              </Text>
            </div>
          </div>
        </div>

        {/* Itinerary: locked (itinerary page/URL), optional link (other), or
            hidden (product/operation/preview — the entity IS the reference). */}
        {lockedItineraryId ? (
          <div className={css.field}>
            <Text as="label" size="sm" weight="medium">
              Itinerary
            </Text>
            <div className={css.locked}>
              <MapPin className={css.lockedIcon} />
              <Text weight="medium">
                {scope.itineraryRef ?? 'Current itinerary'}
              </Text>
            </div>
          </div>
        ) : showItineraryPicker ? (
          <div className={css.field}>
            <Text as="label" size="sm" weight="medium">
              Link an itinerary
            </Text>
            <ServerSearchSelect<ItineraryReferenceMatch>
              getOptionLabel={(i) => itineraryOptionLabel(i)}
              getOptionValue={(i) => i.id}
              onChange={(id) => setPickedItineraryId(id)}
              placeholder="Select or search an itinerary…"
              selectedLabel={pickedItineraryLabel}
              usePortal
              useSearch={makeSeededItinerarySearch(detectedItineraryIds)}
              value={pickedItineraryId}
            />
            <Text as="span" className={css.help} size="xs" variant="muted">
              {detectedItineraryIds.length > 0
                ? 'Optional — pick one detected on this page, or search.'
                : 'Optional — search to attach this incident to an itinerary.'}
            </Text>
          </div>
        ) : null}

        <div className={css.row}>
          <div className={css.field}>
            <Text as="label" size="sm" weight="medium">
              Category <Required />
            </Text>
            <Select
              onChange={(e) => setCategory(e.target.value)}
              options={CATEGORY_OPTIONS}
              placeholder="Select a category"
              usePortal
              value={category}
            />
          </div>
          <div className={css.field}>
            <Text as="label" size="sm" weight="medium">
              Severity <Required />
            </Text>
            <Select
              onChange={(e) => setSeverity(e.target.value)}
              options={SEVERITY_OPTIONS}
              placeholder="Select a severity"
              usePortal
              value={severity}
            />
          </div>
        </div>

        <div className={css.field}>
          <Text as="label" size="sm" weight="medium">
            Country
          </Text>
          <Input
            onChange={(e) => setCountry(e.target.value)}
            placeholder="Auto-filled from the itinerary"
            value={country}
          />
        </div>

        <div className={css.field}>
          <Text as="label" size="sm" weight="medium">
            Describe the incident <Required />
          </Text>
          <Textarea
            onChange={(e) => setWhatHappened(e.target.value)}
            placeholder="What happened, where, and the impact on the trip or client?"
            rows={4}
            value={whatHappened}
          />
        </div>

        {/* Attachments — manual upload only (never auto-captured). Hidden
            from the UI for now (SHOW_ATTACHMENTS_UI) until the final
            attachment experience ships; also hidden when file storage
            (UploadThing) is not configured. Upload wiring is untouched. */}
        {SHOW_ATTACHMENTS_UI && uploadsEnabled ? (
          <div className={css.field}>
            <Text as="label" size="sm" weight="medium">
              Attachments
            </Text>
            <input
              accept="image/*,video/*"
              className={css.hiddenInput}
              multiple
              onChange={onPickFiles}
              ref={fileInputRef}
              type="file"
            />
            <Button
              disabled={isUploading}
              icon={<Paperclip style={{ height: 14, width: 14 }} />}
              onClick={() => fileInputRef.current?.click()}
              size="sm"
              variant="outline"
            >
              {isUploading ? 'Uploading…' : 'Add files'}
            </Button>
            <Text as="span" className={css.help} size="xs" variant="muted">
              Optional. Images ≤ 16MB, documents ≤ 32MB, audio ≤ 64MB, video ≤
              256MB. Nothing is captured automatically.
            </Text>
            {attachments.length > 0 ? (
              <ul className={css.attachments}>
                {attachments.map((a) => (
                  <li className={css.attachment} key={a.key}>
                    {/* Only images have a meaningful thumbnail; every other
                        kind gets an icon so a .docx doesn't render as a broken
                        image. */}
                    {isVideoName(a.name) ? (
                      <Video className={css.attachmentIcon} />
                    ) : isAudioName(a.name) ? (
                      <AudioLines className={css.attachmentIcon} />
                    ) : isDocumentName(a.name) ? (
                      <FileText className={css.attachmentIcon} />
                    ) : (
                      <img
                        alt={a.name}
                        className={css.attachmentThumb}
                        src={a.url}
                      />
                    )}
                    <Text
                      as="span"
                      className={css.attachmentName}
                      size="xs"
                      title={a.name}
                    >
                      {a.name}
                    </Text>
                    <button
                      aria-label={`Remove ${a.name}`}
                      className={css.attachmentRemove}
                      onClick={() => removeAttachment(a.key)}
                      type="button"
                    >
                      <X style={{ height: 14, width: 14 }} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
      <ModalFooter>
        <Button onClick={closeReport} variant="ghost">
          Cancel
        </Button>
        <Button
          disabled={!canSubmit || createIncident.isPending || isUploading}
          onClick={handleSubmit}
          variant="primary"
        >
          {createIncident.isPending ? 'Reporting…' : 'Report'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
